/**
 * Platform Detection Utilities
 * Detects if app is running as native mobile app (Capacitor) or web browser
 */

/**
 * Check if running as native mobile app
 * @returns {boolean} true if running in Capacitor mobile app, false for web browsers
 */
export const isMobileApp = () => {
  return typeof window !== 'undefined' && window.Capacitor !== undefined;
};

/**
 * Get current platform
 * @returns {string} 'ios', 'android', or 'web'
 */
export const getPlatform = () => {
  if (typeof window !== 'undefined' && window.Capacitor !== undefined) {
    return window.Capacitor.getPlatform(); // 'ios' or 'android'
  }
  return 'web';
};

/**
 * Check if running on iOS mobile app
 * @returns {boolean}
 */
export const isIOSApp = () => {
  return isMobileApp() && getPlatform() === 'ios';
};

/**
 * Check if running on Android mobile app
 * @returns {boolean}
 */
export const isAndroidApp = () => {
  return isMobileApp() && getPlatform() === 'android';
};

/**
 * Open external URL in browser (for mobile app payments)
 * For web browsers, opens in new tab
 * For mobile apps, opens in system browser with auto-close
 * @param {string} url - URL to open
 */
export const openExternalBrowser = async (url) => {
  if (isMobileApp()) {
    // Use Capacitor Browser plugin for mobile apps
    try {
      // Dynamic import to avoid build errors when Capacitor is not installed
      if (window.Capacitor && window.Capacitor.Plugins && window.Capacitor.Plugins.Browser) {
        const Browser = window.Capacitor.Plugins.Browser;
        await Browser.open({ 
          url,
          windowName: '_blank',
          toolbarColor: '#000000'
        });
      } else {
        // Fallback if Browser plugin not available
        window.open(url, '_blank');
      }
    } catch (error) {
      console.error('Failed to open browser:', error);
      // Fallback to window.open
      window.open(url, '_blank');
    }
  } else {
    // For web browsers, open in new tab
    window.open(url, '_blank');
  }
};

/**
 * Get webapp URL with auto-login token
 * @param {string} path - Path to append to webapp URL (e.g., '/pricing')
 * @returns {Promise<string>} Full URL with auth token
 */
export const getWebappUrlWithAuth = async (path = '') => {
  const WEBAPP_URL = 'https://nerdnation.world';
  const token = localStorage.getItem('token');
  
  if (!token) {
    // If no token, just return webapp URL
    return `${WEBAPP_URL}${path}`;
  }
  
  try {
    // Generate temporary auth token for secure mobile-to-web transition
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    const response = await fetch(`${API_URL}/api/mobile/generate-temp-token`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
      // No body needed - user_id is extracted from Authorization token on backend
    });
    
    if (response.ok) {
      const data = await response.json();
      return `${WEBAPP_URL}/auth/mobile-login?token=${data.temp_token}&redirect=${encodeURIComponent(path)}`;
    }
  } catch (error) {
    console.error('Failed to generate temp token:', error);
  }
  
  // Fallback: use regular token in URL (less secure but works)
  return `${WEBAPP_URL}/auth/mobile-login?token=${token}&redirect=${encodeURIComponent(path)}`;
};

/**
 * Open webapp with auto-login for mobile app payments.
 * Embeds the user's JWT token in the URL so the website logs them in automatically —
 * no second login required.
 * @param {string} path - Optional path to navigate to (e.g., '/buy-credits', '/pricing')
 */
export const openWebappWithAuth = async (path = '') => {
  if (!isMobileApp()) {
    // On web browser, just navigate normally
    if (path) {
      window.location.href = path;
    }
    return;
  }

  const WEBAPP_URL = 'https://nerdnation.world';
  const token = localStorage.getItem('token');
  const targetPath = path || '/';

  let fullUrl;
  if (token) {
    // Pass token so the website auto-logs the user in (App.js reads source=mobile_app&token=...)
    fullUrl = `${WEBAPP_URL}${targetPath}?source=mobile_app&token=${encodeURIComponent(token)}`;
  } else {
    fullUrl = `${WEBAPP_URL}${targetPath}`;
  }

  await openExternalBrowser(fullUrl);
};
